import { useState } from 'react';

const servicesFaqs = [
  {
    q: 'How long does PPF installation take?',
    a: 'Full vehicle PPF installation takes 3–5 business days depending on vehicle size and package. A front-end partial package typically takes 1–2 days. We use computer-cut precision patterns which speeds up installation while maintaining exacting quality. Your vehicle must be clean and available for pick-up only after full cure.',
  },
  {
    q: 'Can you install PPF on any vehicle?',
    a: 'Yes — we install PPF on sedans, SUVs, coupes, trucks, exotics, and motorcycles. Every vehicle gets a custom-cut pattern from our XPEL software library. If your vehicle model isn\'t in the database, we hand-template a pattern on-site. No vehicle is too complex for our certified technicians.',
  },
  {
    q: 'What\'s included in your ceramic coating service?',
    a: 'All ceramic coating packages include a full paint inspection, clay bar decontamination, and paint decontamination wash. If your paint has swirl marks or scratches, we recommend paint correction beforehand — we offer both as a combined package. The coating itself is applied in our controlled studio environment for perfect bonding results.',
  },
  {
    q: 'Do you offer package deals combining PPF and ceramic coating?',
    a: 'Yes — our most popular offering is our PPF + Ceramic Combo Package. PPF goes on high-impact zones like the front bumper, hood, and mirrors, while ceramic coating is applied over the entire vehicle for hydrophobic protection and gloss. This combo provides the most comprehensive paint protection available.',
  },
  {
    q: 'How do I prepare my car before bringing it in?',
    a: 'Simply give your vehicle a standard exterior wash. Do not apply wax, sealant, or any product before arriving. We handle all professional decontamination, clay bar, and prep work in-house before any film or coating is applied. This ensures optimal bonding and a flawless finished result.',
  },
  {
    q: 'What brands of PPF and coatings do you use?',
    a: 'We install exclusively XPEL and SunTek paint protection film, and apply Ceramic Pro and XPEL Fusion coating systems. We do not use budget or unknown brands — every product we install carries full manufacturer warranties and comes from the world\'s leading automotive protection companies.',
  },
  {
    q: 'Is window tint legal in Connecticut?',
    a: 'Yes — all window tint we install is fully CT-legal. Connecticut law requires 35% VLT on front side windows and has no restriction on rear windows. We carry shades from light 50% VLT to deep 20% VLT for rear windows. We\'ll guide you to the right legal shade for your vehicle.',
  },
  {
    q: 'How does windshield protection film work?',
    a: 'Windshield protection film is a high-impact, optically clear film applied to the exterior of your windshield. It absorbs the energy of stone chips and debris impacts — reducing or eliminating chips and cracks. The film is sacrificial: if it chips, you replace only the film, not the entire windshield. It saves significant money over time.',
  },
];

export default function ServicesFAQ() {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section className="bg-[#080808] py-28" id="faq">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
          <div className="lg:col-span-2">
            <span className="text-[#FF6B35] text-xs font-black uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
              <span className="w-6 h-px bg-[#FF6B35]" />
              Service FAQ
            </span>
            <h2 className="font-display font-black text-4xl lg:text-5xl text-white leading-tight mb-6">
              Service
              <br />
              <span className="text-[#FF6B35]">Questions Answered</span>
            </h2>
            <p className="text-gray-400 text-base leading-relaxed mb-8">
              Not sure which service is right for your vehicle? Call us directly — we'll walk you through the perfect protection plan at no charge.
            </p>

            <div className="bg-[#111111] border border-white/10 rounded-2xl p-6 mb-4">
              <div className="w-8 h-8 flex items-center justify-center mb-3">
                <i className="ri-search-eye-line text-[#FF6B35] text-xl" />
              </div>
              <h3 className="text-white font-bold text-base mb-2">Which protection is right for me?</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                If your vehicle is new or recently detailed, start with PPF on high-impact zones plus ceramic coating for full coverage. If you drive a daily driver or commuter, window tint and ceramic coating are excellent starting points. We customize every recommendation to your vehicle and budget.
              </p>
            </div>
            <div className="bg-[#111111] border border-white/10 rounded-2xl p-6">
              <div className="w-8 h-8 flex items-center justify-center mb-3">
                <i className="ri-tools-line text-[#FF6B35] text-xl" />
              </div>
              <h3 className="text-white font-bold text-base mb-2">The DBN Garage installation process</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Every service at DBN Garage follows the same quality-first protocol: consultation, vehicle prep, decontamination, precision installation, quality inspection, and delivery with full care documentation. We never rush. We never cut corners.
              </p>
            </div>
          </div>

          <div className="lg:col-span-3 space-y-3">
            {servicesFaqs.map((faq, i) => (
              <div key={i}
                className={`rounded-xl border transition-all ${open === i ? 'border-[#FF6B35]/40 bg-[#111111]' : 'border-white/8 bg-[#0E0E0E] hover:border-white/15'}`}>
                <button onClick={() => setOpen(open === i ? null : i)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer">
                  <span className={`font-semibold text-base leading-snug ${open === i ? 'text-[#FF6B35]' : 'text-white/80'}`}>{faq.q}</span>
                  <div className={`w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-full transition-colors ${open === i ? 'bg-[#FF6B35] text-white' : 'bg-white/8 text-gray-500'}`}>
                    <i className={`${open === i ? 'ri-subtract-line' : 'ri-add-line'} text-base`} />
                  </div>
                </button>
                {open === i && (
                  <div className="px-6 pb-5">
                    <p className="text-gray-400 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}

            <div className="mt-6 bg-[#FF6B35]/8 border border-[#FF6B35]/20 rounded-xl p-5">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-mic-line text-[#FF6B35] text-xl" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm mb-1">How long does DBN Garage's installation take?</p>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    Installation times at DBN Garage vary by service: ceramic coating takes 1–2 days, window tint takes 2–4 hours, and full vehicle PPF takes 3–5 business days. We work by appointment at our South Windsor, CT studio and provide transparent timelines upfront — always.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
